/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    createLogPrefix,
    Deferred,
    logDebug,
    type LogTarget,
    noop,
    ValidationError,
} from '@amazon/vinyl-util'
import type { AuthTokenProvider } from '@amzn/vinyl-amzn'
import { type Auth, AuthStatus } from '@/auth/auth'
import { logStatusTransition } from '@/auth/authLog'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

declare global {
    interface Window {
        setAuthToken?: (authToken: string) => void
    }
}

export class MapAuth implements Auth, LogTarget {
    logPrefix = createLogPrefix('MapAuth')

    private _authToken: string | null = null
    private authTokenSet = new Deferred<void>()
    private _status = data(AuthStatus.PENDING)

    constructor() {
        this.authTokenSet
            .then(() => logStatusTransition(this, this._status, AuthStatus.SIGNED_IN))
            .catch(noop)
        window.setAuthToken = (authToken: string) => {
            // Presence only — never log the token value.
            logDebug(this, 'setAuthToken')
            this._authToken = authToken
            this.authTokenSet.resolve(void 0)
        }
    }

    get status(): ObservableValue<AuthStatus> {
        return this._status
    }

    isSignedIn(): boolean {
        return this._authToken !== null
    }

    usesBearer(): boolean {
        return true
    }

    get authTokenProvider(): AuthTokenProvider {
        return async (): Promise<string> => {
            await this.authTokenSet
            if (!this._authToken) throw new ValidationError('authToken was set, but undefined')
            return this._authToken
        }
    }

    /**
     * For Map auth, this does nothing.
     */
    signIn() {
        logDebug(this, 'signIn (no-op)')
    }

    /**
     * For Map auth, this does nothing.
     */
    signOut() {
        logDebug(this, 'signOut (no-op)')
    }
}
